import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { fetchRetention, invalidateRetention, RETENTION_FALLBACK } from '../services/retentionService';

/**
 * Tenant retention context.
 *
 * <p>Provides:
 * <ul>
 *   <li>{@code days} — { rum, logs, traces, metrics } in days</li>
 *   <li>{@code max} — the broadest window across all categories</li>
 *   <li>{@code maxDaysFor(dataType)} — helper used by the time-range
 *       picker. {@code dataType} undefined / unknown falls back to
 *       {@code max} so generic pages don't accidentally hide
 *       options.</li>
 *   <li>{@code refresh()} — force a refresh; called from the operator
 *       panel after a plan edit.</li>
 * </ul>
 *
 * <p>The fetch happens once on provider mount. The result is cached in
 * sessionStorage by the underlying service, so a page reload doesn't
 * trigger another network round-trip until the 5-minute TTL elapses.
 */

const RetentionContext = createContext(null);

export const useRetention = () => {
  const ctx = useContext(RetentionContext);
  if (!ctx) {
    // Dev-time guard — every page that reads retention sits inside a
    // <RetentionProvider> in App.jsx. If you ever see this in prod
    // somebody added a route without wrapping it.
    throw new Error('useRetention must be used within a RetentionProvider');
  }
  return ctx;
};

export const RetentionProvider = ({ children }) => {
  const [state, setState] = useState({
    payload: RETENTION_FALLBACK,
    loading: true,
  });

  useEffect(() => {
    let cancelled = false;
    fetchRetention()
      .then((payload) => {
        if (!cancelled) setState({ payload, loading: false });
      })
      .catch(() => {
        if (!cancelled) setState({ payload: RETENTION_FALLBACK, loading: false });
      });
    return () => { cancelled = true; };
  }, []);

  const value = useMemo(() => {
    const { payload, loading } = state;
    const days = payload?.days || RETENTION_FALLBACK.days;
    const max = payload?.max || RETENTION_FALLBACK.max;

    return {
      days,
      max,
      loading,
      tenantScoped: !!payload?.tenantScoped,
      fallback: !!payload?.fallback,
      /**
       * Resolve the cap (in days) for a specific category.
       * Unknown / missing category → {@code max} so the picker still
       * works as a generic time picker on shared pages.
       */
      maxDaysFor(dataType) {
        if (!dataType) return max;
        const v = days[dataType];
        return typeof v === 'number' && v > 0 ? v : max;
      },
      refresh: async () => {
        invalidateRetention();
        const fresh = await fetchRetention({ force: true });
        setState({ payload: fresh, loading: false });
      },
    };
  }, [state]);

  return (
    <RetentionContext.Provider value={value}>
      {children}
    </RetentionContext.Provider>
  );
};

export default RetentionContext;
